import { useState } from 'react';
import { Field } from 'formik';

interface FloatingLabelInputProps {
  name: string;
  label: string;
  type?: string;
  icon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  onRightIconClick?: () => void;
  placeholder?: string;
  inputMode?: 'text' | 'decimal' | 'numeric' | 'tel' | 'search' | 'email' | 'url';
  pattern?: string;
  readOnly?: boolean;
  onPaste?: (e: React.ClipboardEvent<HTMLInputElement>) => void;
}

export default function FloatingLabelInput({
  name,
  label,
  type = 'text',
  icon,
  rightIcon,
  onRightIconClick,
  placeholder,
  inputMode,
  pattern,
  readOnly,
  onPaste,
}: FloatingLabelInputProps) {
  const [isFocused, setIsFocused] = useState(false);

  return (
    <Field name={name}>
      {({ field, meta }: any) => {
        const hasValue = field.value && field.value.length > 0;
        const showLabel = isFocused || hasValue;
        const hasError = meta.touched && meta.error;

        return (
          <div className="relative">
            {/* Floating Label */}
            <label
              htmlFor={name}
              className={`absolute z-10 left-[2rem] bg-[#F9F9F9] dark:bg-[#1A1A1A] px-[0.8rem] text-[#BFBFBF] transition-all duration-200 pointer-events-none
                ${showLabel
                  ? 'top-[-1rem] text-[1.4rem] text-[#57E8BA]'
                  : 'top-[50%] -translate-y-1/2 text-[1.6rem]'
                }
              `}
              style={{
                opacity: showLabel ? 1 : 0,
              }}
            >
              {label}
            </label>

            {/* Input Container */}
            <div
              className={`flex items-center relative rounded-[1.2rem] px-[1.2rem] py-[0.8rem] border-[0.2rem] transition-all duration-200
                ${isFocused
                  ? 'border-[#57E8BA]'
                  : hasError
                    ? 'border-red-500'
                    : 'border-[#7A7A7A] dark:border-[#3A3A3A]'
                }
                bg-transparent
              `}
            >
              {/* Left Icon */}
              {icon && <div className="mr-[1.2rem] flex-shrink-0">{icon}</div>}

              {/* Input Field */}
              <input
                {...field}
                id={name}
                type={type}
                inputMode={inputMode}
                pattern={pattern}
                placeholder={isFocused ? '' : placeholder || label}
                onFocus={() => setIsFocused(true)}
                onBlur={(e) => {
                  setIsFocused(false);
                  field.onBlur(e);
                }}
                onPaste={onPaste}
                readOnly={readOnly}
                className={`flex-1 bg-transparent text-[#000000] dark:text-[#FFFFFF] placeholder-[#7A7A7A] text-[1.6rem] placeholder:text-[1.6rem] focus:outline-none ${readOnly ? 'cursor-not-allowed' : ''}`}
                autoComplete="off"
              />

              {/* Right Icon */}
              {rightIcon && (
                <button
                  type="button"
                  onClick={onRightIconClick}
                  className="ml-[1.2rem] absolute right-[1rem] flex-shrink-0 focus:outline-none"
                >
                  {rightIcon}
                </button>
              )}
            </div>

            {/* Error Message */}
            {hasError && (
              <div className="text-red-400 text-[1.4rem] mt-[0.8rem] ml-[2rem]">{meta.error}</div>
            )}
          </div>
        );
      }}
    </Field>
  );
}
